import { MapPin, Scissors, Shirt, Layers, PackageCheck } from "lucide-react";
import { TEXTILE_FACILITY, IMAGES } from "@/data/site";
import { TEXTILE_AR } from "@/data/content_ar";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { LogoMark } from "@/components/LogoMark";
import { useI18n } from "@/context/I18nContext";

const icons = [Scissors, Shirt, Layers, PackageCheck];

export default function TextileFacility() {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const heading = ar ? TEXTILE_AR.heading : TEXTILE_FACILITY.heading;
  const body = ar ? TEXTILE_AR.body : TEXTILE_FACILITY.body;
  const caps = ar ? TEXTILE_AR.caps : TEXTILE_FACILITY.capabilities;
  return (
    <section id="textiles-mfg" data-testid="textile-facility-section" className="relative z-10">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="group relative overflow-hidden rounded-2xl border border-brand-border aspect-[4/5] shadow-sm">
              <img src={IMAGES.weave} alt="Textile manufacturing at Palamaner" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent" />
              <LogoMark className="absolute top-5 left-5 z-10" />
              <div className="absolute bottom-6 left-6 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-4 py-2 text-sm text-brand-gold font-medium">
                <MapPin size={15} /> {TEXTILE_FACILITY.location}
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal><Eyebrow>{t("tex_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-fg leading-[1.02]">
                {heading}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-4 flex items-center gap-2 text-sm text-brand-gold"><MapPin size={15} /> {TEXTILE_FACILITY.locationNote}</div>
            </Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-brand-fg/75 leading-relaxed">{body}</p></Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {caps.map((c, i) => {
                const Icon = icons[i];
                return (
                  <Reveal key={c.title} delay={0.12 + i * 0.06}>
                    <div className="group flex gap-4 rounded-2xl border border-brand-border bg-brand-card p-5 hover:border-brand-gold/50 transition-colors h-full shadow-sm">
                      <span className="grid place-items-center h-11 w-11 shrink-0 rounded-full bg-brand-goldSoft text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors">
                        <Icon size={18} />
                      </span>
                      <div>
                        <h3 className="font-display text-lg text-brand-fg">{c.title}</h3>
                        <p className="mt-1 text-sm text-brand-fg/65 leading-relaxed">{c.body}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
